/**
 * Click to Copy: <uo-click-to-copy>
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
// Import global utils
import { createSnackbar } from '@automator/shared/utils/create-snackbar';
// Import WordPress utils
import { escapeHTML } from '@wordpress/escape-html';
import { __, sprintf } from '@wordpress/i18n';
// Import style for web component
import styles from './web-component-style.scss?inline';
/**
 * Duration in milliseconds that notifications are displayed.
 */
const SNACKBAR_DURATION = 3000;
/**
 * Button state constants.
 */
const BUTTON_STATES = {
    IDLE: 'idle',
    COPIED: 'copied',
};
/**
 * Button color constants.
 */
const BUTTON_COLORS = {
    DEFAULT: '',
    SUCCESS: 'success',
};
/**
 * Icon identifier constants.
 */
const ICONS = {
    COPY: 'copy',
    CHECK: 'check',
    TIMES: 'times',
};
/**
 * @element uo-click-to-copy
 * @summary A button component that copies content to the clipboard with visual feedback.
 * @description This component provides a click-to-copy button that uses the Clipboard API to copy dynamic content.
 * It displays success/error notifications and provides visual state feedback through button color and icon changes.
 *
 * @property {Function} getContent - Function that returns the content to copy. Must be set via property binding (`.getContent`), not as an attribute. Called at click time to ensure fresh content. Should return a string or undefined.
 *
 * @attr {string} [content-name] - Human-readable name of the content being copied. Used to generate the tooltip and success message. Maps to the `contentName` property.
 * @attr {string} [tooltip] - Custom tooltip text to display on hover. If not provided, a default tooltip is generated. Maps to the `tooltip` property.
 *
 * @cssprop {string} [--button-color] - Color of the copy button in its default state.
 * @cssprop {string} [--button-hover-color] - Color of the button on hover.
 */
let AutomatorClickToCopy = class AutomatorClickToCopy extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Current state of the button.
         * Controls visual feedback after successful copy operation.
         * @private
         */
        this.buttonState = BUTTON_STATES.IDLE;
        /**
         * Tracks whether a snackbar notification is currently being displayed.
         * Prevents multiple overlapping snackbars from appearing.
         * @private
         */
        this.isShowingSnackbar = false;
    }
    /**
     * Renders the component's DOM structure based on its properties.
     * @return {TemplateResult} The HTML template for the component.
     */
    render() {
        const buttonStyle = this.getButtonStyle();
        return html `
			<uo-tooltip>
				<span class="nowrap">${this.getTooltipText()}</span>
				<span slot="target">
					<uo-button
						@click="${this.handleClick}"
						color=${buttonStyle.color}
						size="small"
					>
						<uo-icon id="${buttonStyle.icon}"></uo-icon>
					</uo-button>
				</span>
			</uo-tooltip>
		`;
    }
    /**
     * Computes the button's visual style based on current state.
     * @return {ButtonStyle} The button styling configuration.
     * @private
     */
    getButtonStyle() {
        if (this.buttonState === BUTTON_STATES.COPIED) {
            return {
                color: BUTTON_COLORS.SUCCESS,
                icon: ICONS.CHECK,
            };
        }
        return {
            color: BUTTON_COLORS.DEFAULT,
            icon: ICONS.COPY,
        };
    }
    /**
     * Computes the tooltip text based on available properties.
     * Priority: tooltip prop → generated from contentName → default.
     * @return {string} The tooltip text to display.
     * @private
     */
    getTooltipText() {
        // Use explicit tooltip if provided
        if (this.tooltip) {
            return this.tooltip;
        }
        // Build tooltip from contentName if available
        if (this.contentName) {
            return sprintf(escapeHTML(__('Copy %s to clipboard', 'uncanny-automator')), this.contentName);
        }
        // Default tooltip
        return escapeHTML(__('Click to copy', 'uncanny-automator'));
    }
    /**
     * Handles the button click event.
     * Orchestrates content validation, clipboard copy, and user feedback.
     * @private
     */
    async handleClick() {
        // Validate that getContent is provided and is a function
        if (typeof this.getContent !== 'function') {
            this.showError(escapeHTML(__('Copy failed: No content available', 'uncanny-automator')), 'uo-click-to-copy: getContent must be a function');
            return;
        }
        // Validate and get content
        const content = this.validateContent();
        if (!content) {
            return;
        }
        // Copy to clipboard
        await this.copyToClipboard(content);
    }
    /**
     * Validates and retrieves content from the getContent function.
     * Ensures content is a non-empty string.
     * @return {string | null} The validated content or null if invalid.
     * @private
     */
    validateContent() {
        // Get content at click time (getContent is guaranteed to exist by handleClick)
        const content = this.getContent();
        // Validate content is a non-empty string
        if (typeof content !== 'string' || content.trim().length === 0) {
            this.showError(escapeHTML(__('Copy failed: No content available', 'uncanny-automator')), 'uo-click-to-copy: getContent must return a non-empty string');
            return null;
        }
        return content;
    }
    /**
     * Copies content to the clipboard using the Clipboard API.
     * Performs browser compatibility check and shows appropriate feedback.
     * @param {string} content - The content to copy to the clipboard.
     * @private
     */
    async copyToClipboard(content) {
        // Check if Clipboard API is available
        if (!navigator.clipboard?.writeText) {
            this.showError(escapeHTML(__('Copy failed: Clipboard not available', 'uncanny-automator')), 'Clipboard API not available in this browser/context.');
            return;
        }
        try {
            await navigator.clipboard.writeText(content);
            this.showSuccess();
        }
        catch (err) {
            this.showError(escapeHTML(__('Copy failed: Unable to copy to clipboard', 'uncanny-automator')), `Clipboard copy failed: ${err}`);
        }
    }
    // ============================================================================
    // Notification Methods
    // ============================================================================
    /**
     * Generates the success message text.
     * Uses contentName if provided, otherwise returns a generic message.
     * @return {string} The success message to display.
     * @private
     */
    getSuccessMessage() {
        if (this.contentName) {
            return sprintf(escapeHTML(__('"%s" copied to clipboard', 'uncanny-automator')), this.contentName);
        }
        return escapeHTML(__('Copied to clipboard', 'uncanny-automator'));
    }
    /**
     * Displays a success notification and updates button state.
     * Changes button state to 'copied' temporarily, then resets to 'idle'.
     * @private
     */
    showSuccess() {
        // Prevent multiple snackbars from being shown
        if (this.isShowingSnackbar) {
            return;
        }
        // Update button state to show success
        this.buttonState = BUTTON_STATES.COPIED;
        // Show success notification
        this.flashNotice({
            iconID: ICONS.CHECK,
            message: this.getSuccessMessage(),
        });
    }
    /**
     * Displays an error notification without changing button state.
     * Logs detailed error to console for debugging.
     * @param {string} message - The user-facing error message.
     * @param {string} [consoleError] - Optional detailed error message for console.
     * @private
     */
    showError(message, consoleError) {
        // Log detailed error to console for debugging
        if (consoleError) {
            console.error(consoleError);
        }
        // Force replace any existing snackbar
        if (this.isShowingSnackbar) {
            this.isShowingSnackbar = false;
        }
        // Show error notification
        this.flashNotice({
            iconID: ICONS.TIMES,
            message,
        });
    }
    /**
     * Displays a temporary snackbar notification.
     * Automatically resets component state after the notification duration.
     * @param {Pick<CreateSnackbarOptions, 'iconID' | 'message'>} options - Notification options.
     * @private
     */
    flashNotice(options) {
        // Mark that we're showing a snackbar
        this.isShowingSnackbar = true;
        // Display the snackbar
        createSnackbar({
            iconID: options.iconID,
            message: options.message,
            duration: SNACKBAR_DURATION,
        });
        // Reset state after duration
        setTimeout(() => {
            this.buttonState = BUTTON_STATES.IDLE;
            this.isShowingSnackbar = false;
        }, SNACKBAR_DURATION);
    }
};
/**
 * Applies the component's styles, loading from the imported SCSS file.
 * Uses `unsafeCSS` to allow Lit to process the raw CSS string.
 */
AutomatorClickToCopy.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Function, attribute: false })
], AutomatorClickToCopy.prototype, "getContent", void 0);
__decorate([
    property({ type: String, attribute: 'content-name' })
], AutomatorClickToCopy.prototype, "contentName", void 0);
__decorate([
    property({ type: String })
], AutomatorClickToCopy.prototype, "tooltip", void 0);
__decorate([
    state()
], AutomatorClickToCopy.prototype, "buttonState", void 0);
__decorate([
    state()
], AutomatorClickToCopy.prototype, "isShowingSnackbar", void 0);
AutomatorClickToCopy = __decorate([
    customElement('uo-click-to-copy')
], AutomatorClickToCopy);
export { AutomatorClickToCopy };
